import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Drawer,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useGetEnstruLookupQuery, useGetOktru2LookupQuery, useGetEnstruToOktru2Query, useGetPropertyQuery } from '@/services/api';
import Editor from '@monaco-editor/react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/store';
import { stripEnstruPrefix } from './utils';
import type { EnumValueInfo } from '@/types/api';

const DRAWER_WIDTH = 936;

/** Compact key-value row. */
const Field: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', gap: 2, py: 0.5, minHeight: 28, alignItems: 'baseline' }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ flexShrink: 0, width: 140, textAlign: 'right' }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      component="div"
      sx={{
        flex: 1,
        minWidth: 0,
        fontFamily: mono ? 'monospace' : 'inherit',
        wordBreak: 'break-word',
      }}
    >
      {value || '-'}
    </Typography>
  </Box>
);

/** Section header — thin line with label */
const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{
      display: 'block',
      mt: 2.5,
      mb: 1,
      letterSpacing: 1.2,
      fontSize: '0.7rem',
      borderBottom: 1,
      borderColor: 'divider',
      pb: 0.5,
    }}
  >
    {children}
  </Typography>
);

interface PropertyViewerProps {
  propertyId: string | null;
  onClose: () => void;
}

const PropertyViewer: React.FC<PropertyViewerProps> = ({ propertyId, onClose }) => {
  const { language } = useSelector((state: RootState) => state.ui);
  const [linkCopied, setLinkCopied] = useState(false);

  const {
    data: property,
    error,
    isLoading,
  } = useGetPropertyQuery(propertyId!, { skip: !propertyId });
  const { data: enstruLookup } = useGetEnstruLookupQuery();
  const { data: oktru2Lookup } = useGetOktru2LookupQuery();
  const { data: enstruToOktru2 } = useGetEnstruToOktru2Query();

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Fallback for older browsers / non-HTTPS
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }, []);

  const hasEnumValues = property?.data_type === 'enum' && property?.enum_values?.length > 0;
  const hasExamples = property?.examples && property.examples.length > 0;
  const normalizeEnstruCode = (code: string) => code.replace(/^ENSTRU:/, '');

  // Full 3-segment ENSTRU codes (15 digits, format XXXXXX.XXX.XXXXXX) — only these can map to OKTRU2
  const FULL_ENSTRU_RE = /^\d{6}\.\d{3}\.\d{6}$/;

  // Compute OKTRU2 codes for this property via ENSTRU -> OKTRU2 reverse mapping
  const oktru2Codes = React.useMemo(() => {
    if (!property?.enstru_codes || !enstruToOktru2) return [];

    const seen = new Set<string>();
    const result: Array<{ code: string; name: string }> = [];

    for (const rawCode of property.enstru_codes) {
      const code = normalizeEnstruCode(rawCode);
      // Only map full 3-segment codes — short codes (6-9 digits) can't map unambiguously
      if (!FULL_ENSTRU_RE.test(code)) continue;

      const mapped = enstruToOktru2[code];
      if (!mapped) continue;

      for (const oktru2Code of mapped) {
        if (seen.has(oktru2Code)) continue;
        seen.add(oktru2Code);
        result.push({
          code: oktru2Code,
          name: oktru2Lookup?.[oktru2Code]?.name || '',
        });
      }
    }

    return result;
  }, [property?.enstru_codes, enstruToOktru2, oktru2Lookup]);

  const drawerContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ p: 3 }}>
          <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1 }} />
          <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={300} />
        </Box>
      );
    }

    if (error) {
      const errorMessage = 'data' in error && error.data
        ? (error.data as any).message || 'Failed to load property'
        : 'Failed to load property';

      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <Typography variant="subtitle2" gutterBottom>
              {language === 'en' ? 'Error Loading Property' : 'Ошибка загрузки свойства'}
            </Typography>
            <Typography variant="body2">{errorMessage}</Typography>
          </Alert>
        </Box>
      );
    }

    if (!property) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="warning">
            {language === 'en'
              ? `Property not found: ${propertyId}`
              : `Свойство не найдено: ${propertyId}`}
          </Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ px: 3, pb: 3 }}>
        {/* Names */}
        <SectionHeader>{language === 'en' ? 'Names' : 'Названия'}</SectionHeader>
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>RU</strong></TableCell>
                <TableCell><strong>EN</strong></TableCell>
                <TableCell><strong>KZ</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>{property.canonical_name_ru || '-'}</TableCell>
                <TableCell>{property.canonical_name_en || '-'}</TableCell>
                <TableCell>{property.canonical_name_kz || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Synonyms */}
        {((property.synonyms_ru?.length ?? 0) > 0 || (property.synonyms_kz?.length ?? 0) > 0) && (
          <>
            <SectionHeader>{language === 'en' ? 'Synonyms' : 'Синонимы'}</SectionHeader>
            {property.synonyms_ru && property.synonyms_ru.length > 0 && (
              <Field
                label="RU"
                value={
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {property.synonyms_ru.map((syn, i) => (
                      <Chip key={i} label={syn} size="small" variant="outlined" />
                    ))}
                  </Box>
                }
              />
            )}
            {property.synonyms_kz && property.synonyms_kz.length > 0 && (
              <Field
                label="KZ"
                value={
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {property.synonyms_kz.map((syn, i) => (
                      <Chip key={i} label={syn} size="small" variant="outlined" />
                    ))}
                  </Box>
                }
              />
            )}
          </>
        )}

        {/* Classification */}
        <SectionHeader>{language === 'en' ? 'Classification' : 'Классификация'}</SectionHeader>
        <Field
          label="ID"
          value={
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {stripEnstruPrefix(property.property_id)}
            </Typography>
          }
        />
        <Field
          label={language === 'en' ? 'Data type' : 'Тип данных'}
          value={<Chip label={property.data_type} size="small" variant="outlined" />}
        />
        {property.unit_family && (
          <Field
            label={language === 'en' ? 'Unit family' : 'Семейство'}
            value={property.unit_family}
          />
        )}
        {property.unit_ucum && (
          <Field
            label={language === 'en' ? 'Unit (UCUM)' : 'Единица'}
            value={property.unit_ucum}
            mono
          />
        )}
        {property.unit_allowed && property.unit_allowed.length > 0 && (
          <Field
            label={language === 'en' ? 'Allowed' : 'Допустимые'}
            value={
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {property.unit_allowed.map((u, i) => (
                  <Chip key={i} label={u} size="small" variant="outlined" />
                ))}
              </Box>
            }
          />
        )}

        {/* Enum Values */}
        {hasEnumValues && (
          <>
            <SectionHeader>{language === 'en' ? 'Enum Values' : 'Значения enum'}</SectionHeader>
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'hidden' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}><strong>ID</strong></TableCell>
                    <TableCell><strong>{language === 'en' ? 'Label' : 'Метка'}</strong></TableCell>
                    <TableCell><strong>{language === 'en' ? 'Synonyms' : 'Синонимы'}</strong></TableCell>
                    <TableCell sx={{ width: 60 }}><strong>{language === 'en' ? 'Freq' : 'Част.'}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...property.enum_values].sort((a, b) => b.frequency - a.frequency).map((ev: EnumValueInfo) => (
                    <TableRow key={ev.id} hover>
                      <TableCell sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-word' }}>
                          {ev.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {language === 'en'
                            ? (ev.label_en || ev.label_ru || '-')
                            : (ev.label_ru || ev.label_en || '-')}
                        </Typography>
                        {ev.label_en && ev.label_ru && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {language === 'en' ? ev.label_ru : ev.label_en}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {ev.synonyms_ru && ev.synonyms_ru.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {ev.synonyms_ru.map((s, i) => (
                              <Chip key={i} label={s} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            ))}
                          </Box>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{ev.frequency}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Examples */}
        {hasExamples && (
          <>
            <SectionHeader>{language === 'en' ? 'Examples' : 'Примеры'}</SectionHeader>
            <Box sx={{ display: 'grid', gap: 0.5 }}>
              {property.examples.map((ex, i) => (
                <Typography
                  key={i}
                  variant="body2"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    py: 0.25,
                    pl: 1,
                    borderLeft: 2,
                    borderColor: 'divider',
                  }}
                >
                  {ex}
                </Typography>
              ))}
            </Box>
          </>
        )}

        {/* ENSTRU Codes */}
        {property.enstru_codes && property.enstru_codes.length > 0 && (
          <>
            <SectionHeader>{language === 'en' ? 'ENSTRU Codes' : 'Коды ЕНСТРУ'}</SectionHeader>
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{language === 'en' ? 'Code' : 'Код'}</strong></TableCell>
                    <TableCell><strong>{language === 'en' ? 'Name' : 'Название'}</strong></TableCell>
                    <TableCell><strong>{language === 'en' ? 'Characteristic' : 'Характеристика'}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {property.enstru_codes.map((rawCode, i) => {
                    const code = normalizeEnstruCode(rawCode);
                    const details = enstruLookup?.[code];
                    return (
                      <TableRow key={`${rawCode}-${i}`} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {code}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {details?.name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {details?.characteristic || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {property.catalog_id && (
              <Field
                label={language === 'en' ? 'Catalog ID' : 'ID каталога'}
                value={property.catalog_id}
                mono
              />
            )}
          </>
        )}

        {/* OKTRU2 Codes */}
        {oktru2Codes.length > 0 && (
          <>
            <SectionHeader>{language === 'en' ? 'OKTRU 2.0 Codes' : 'Коды ОКТРУ 2.0'}</SectionHeader>
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{language === 'en' ? 'Code' : 'Код'}</strong></TableCell>
                    <TableCell><strong>{language === 'en' ? 'Name' : 'Название'}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {oktru2Codes.map((entry) => (
                    <TableRow key={entry.code} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                          {entry.code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {entry.name || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* JSON Accordion */}
        <Box sx={{ mt: 3 }}>
          <Accordion variant="outlined" disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                JSON
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Editor
                width="100%"
                height="400px"
                defaultLanguage="json"
                value={JSON.stringify(property, null, 2)}
                options={{
                  readOnly: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontSize: 12,
                  lineHeight: 17,
                  lineNumbers: 'on',
                  folding: true,
                  wordWrap: 'on',
                  automaticLayout: true,
                  padding: { top: 4, bottom: 4 },
                  scrollbar: { vertical: 'auto', horizontal: 'auto' },
                  overviewRulerBorder: false,
                  hideCursorInOverviewRuler: true,
                  overviewRulerLanes: 0,
                }}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={!!propertyId}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', md: DRAWER_WIDTH },
          maxWidth: '100%',
        },
      }}
    >
      {/* Sticky header */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
            {isLoading ? (
              <Skeleton variant="text" width="70%" height={28} />
            ) : property ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="h6" component="h1" sx={{ lineHeight: 1.3 }}>
                    {language === 'en'
                      ? (property.canonical_name_en || property.canonical_name_ru || stripEnstruPrefix(property.property_id))
                      : (property.canonical_name_ru || property.canonical_name_en || stripEnstruPrefix(property.property_id))}
                  </Typography>
                  <Chip
                    label={(property.frequency ?? 0).toLocaleString()}
                    size="small"
                    variant="outlined"
                  />
                  {property.deprecated && (
                    <Chip label="deprecated" size="small" color="error" />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {property.updated_at ? property.updated_at.split('T')[0] : ''}
                  </Typography>
                </Box>
                {property.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    {property.description}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="h6" color="text.secondary">
                {language === 'en' ? 'Property' : 'Свойство'}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, mt: -0.5 }}>
            <Tooltip title={linkCopied ? (language === 'en' ? 'Copied!' : 'Скопировано!') : (language === 'en' ? 'Copy link' : 'Скопировать ссылку')}>
              <IconButton onClick={handleCopyLink} aria-label="copy link" size="small">
                {linkCopied ? <CheckIcon color="success" /> : <LinkIcon />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} edge="end" aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Scrollable content */}
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {drawerContent()}
      </Box>
    </Drawer>
  );
};

export default PropertyViewer;
